import { type PropsWithChildren } from 'react';

import { XYChartDataContext } from '../context/XYChartData.context.js';
import { XYChartLinearSelectionContext } from '../context/XYChartLinearSelection.context.js';
import { XYChartRectDataContext } from '../context/XYChartRectData.context.js';
import { XYChartRectSelectionContext } from '../context/XYChartRectSelection.context.js';
import { useColorRule } from '../hooks/useColorRule.js';
import { useXYChartDataProvider } from '../hooks/useXYChartDataProvider.js';
import type { XYChartVariantsInternal } from '../types/xy-chart-internals.js';
import { type XYChartData } from '../types/xy-chart.js';

interface XYChartChartDataProviderProps {
  seriesVariantsProps: XYChartVariantsInternal[];
  data: XYChartData[];
}

export const XYChartChartDataProvider = ({
  seriesVariantsProps,
  data,
  children,
}: PropsWithChildren<XYChartChartDataProviderProps>) => {
  const {
    axes,
    axisIdToScaleDomain,
    axisIdToCategoryDatum,
    seriesIdToValueScaleDomain,
    axesMetadata,
    datapointMetadataMap,
    linearSelectionContextValue,
    rectSelectionContextValue,
    seriesWithDatapoints,
    seriesToMetadata: metadataWithoutColorRules,
    axisIdToDimension,
    filteredSeriesWithDatapoints,
  } = useXYChartDataProvider(seriesVariantsProps, data);

  const seriesToMetadata = useColorRule({
    seriesWithDatapoints,
    seriesToMetadata: metadataWithoutColorRules,
    datapointMetadataMap,
  });

  return (
    <XYChartDataContext.Provider
      value={{
        axesMetadata,
        axes,
        seriesWithDatapoints,
        filteredSeriesWithDatapoints,
        seriesToMetadata,
        axisIdToScaleDomain,
        axisIdToCategoryDatum,
        datapointMetadataMap,
        axisIdToDimension,
      }}
    >
      <XYChartRectDataContext.Provider
        value={{
          seriesIdToValueScaleDomain,
        }}
      >
        <XYChartLinearSelectionContext.Provider
          value={linearSelectionContextValue}
        >
          <XYChartRectSelectionContext.Provider
            value={rectSelectionContextValue}
          >
            {children}
          </XYChartRectSelectionContext.Provider>
        </XYChartLinearSelectionContext.Provider>
      </XYChartRectDataContext.Provider>
    </XYChartDataContext.Provider>
  );
};

XYChartChartDataProvider['displayName'] = 'XYChartChartDataProvider';
